import React from "react";
import cns from "classnames";
import Body from "@components/typography/Body";
import { EventLog_EventFragment } from "@graphql";
import { toLocale } from "@bonliva-crm/locale";
import { ThemeType } from "@theme";
import UserAvatar from "@containers/UserAvatar";

import { makeStyles } from "@material-ui/styles";

export type EventProps = {
  item: EventLog_EventFragment;
  extraClasses?: {
    root?: string;
    content?: string;
  };
  extraContent?: React.ReactNode;
  icon?: React.ReactNode;
  floatingMenu?: React.ReactElement<{ className: string }>;
  subtitleRenderer?: (createdOn: string) => React.ReactNode;
};

type Props = EventProps & {
  children?: React.ReactNode;
};

export function Event(props: Props) {
  const classes = useStyles({});
  const { children, extraClasses = {}, extraContent, icon, item } = props;

  const renderAvatar = () => {
    return item.CreatedBy ? (
      <UserAvatar userName={item.CreatedBy.Name} size="small" />
    ) : null;
  };

  const renderMenu = () => {
    if (!props.floatingMenu) {
      return null;
    }

    return React.cloneElement(props.floatingMenu, {
      className: cns(classes.icons, classes.menu)
    });
  };

  const renderSubtitle = (createdOn: string) => {
    if (props.subtitleRenderer) {
      return props.subtitleRenderer(createdOn);
    }

    if (!createdOn) {
      return null;
    }

    return <Body>{createdOn}</Body>;
  };

  const rootClassName = cns(classes.root, extraClasses.root, {
    [classes.sticky]: item.Sticky,
    [classes.warning]: item.Warning
  });
  const contentClassName = cns(classes.content, extraClasses.content);

  const name = item.CreatedBy ? item.CreatedBy.Name : null;

  return (
    <div className={rootClassName}>
      {renderAvatar()}

      <div className={contentClassName}>
        <div className={classes.titleSection}>
          {name && (
            <Body style={{ fontWeight: "bold" }} className="hl">
              {name}
            </Body>
          )}
          {renderSubtitle(toLocale.dateTime(item.CreatedOn))}
        </div>

        {children}
      </div>

      {extraContent}

      {icon && <div className={classes.activityIcon}>{icon}</div>}

      {renderMenu()}
    </div>
  );
}

const useStyles = makeStyles((theme: ThemeType) => ({
  root: {
    display: "flex",
    padding: 8,
    paddingLeft: 16,
    paddingRight: 16,
    position: "relative",

    "& > *": {
      marginRight: theme.spacing(2)
    },

    "&:not(:last-child)": {
      borderBottom: `1px solid ${theme.separatorColor}`,
      padding: theme.spacing()
    },

    "&:hover $menu": {
      opacity: 1
    }
  },

  icons: {
    display: "flex",
    position: "absolute",
    right: 0,

    "&:not(:last-child)": {
      borderBottom: `1px solid ${theme.separatorColor}`,
      padding: theme.spacing()
    },

    "&:hover $menu": {
      opacity: 1
    }
  },

  sticky: {
    borderLeft: `3px solid ${theme.palette.alter.main}`,
    background: theme.comments.sticky.background,
    marginBottom: theme.spacing()
  },

  warning: {
    "&&": {
      background: theme.palette.error.background
    }
  },

  content: {
    flex: "1 0 0"
  },

  menu: {},

  titleSection: {
    ...theme.mixins.childrenMarginRight(theme.spacing(1 / 2)),
    alignItems: "baseline",
    display: "flex",
    paddingBottom: theme.spacing()
  },

  activityIcon: {
    alignSelf: "center",
    "& > *": {
      width: 32,
      height: 32,
      color: theme.palette.grey[700]
    }
  }
}));
